import {
  faBriefcase,
  faCalculator,
  faCircleUser,
  faCode,
  faCube,
  faDiagramNext,
  faEquals,
  faMicrochip,
  faRobot,
  faShuffle,
  faSparkles,
  faSplit,
  faTable,
  faTableRows,
} from "@fortawesome/pro-regular-svg-icons";
import { faRotateRight } from "@fortawesome/pro-solid-svg-icons";
import { Edge } from "@xyflow/react";
import { observer } from "mobx-react-lite";

import { Provider, Resource } from "src/api/connectApi/types";
import { ProviderIcon } from "src/connections/config/Icon";
import { ResourceIcon } from "src/connections/ResourceIcon";
import {
  BeMappedNode,
  CustomConnectionNodeData,
  DatabaseConnectionNodeData,
  InboundWebhookConnectionNodeData,
  IntegrationNodeData,
  ManifestConnectionNodeData,
} from "src/constants/NodeDataTypes";
import {
  AddNodeGroupType,
  ConnectionGroupType,
  ConnectNodeType,
  NODE_TYPE,
  SIMPLE_CREATABLE_NODE_TYPES,
  SimpleCreatableNodeType,
} from "src/constants/NodeTypes";
import {
  NodeIcon,
  NodeIconColor,
  NodeIconSize,
} from "src/design-system/NodeIcon";
import { getCreatableNodes } from "src/nodeAdding/PasteNodesCard";
import { useGraphStore } from "src/store/StoreProvider";
import {
  getEdgesInSubgraph,
  orderNodesAndEdgesForLayout,
} from "src/utils/GraphUtils";
import { assertUnreachable } from "src/utils/typeUtils";

const getCreatableChildrenOrdered = (
  childrenNodeIDs: string[],
  allNodes: BeMappedNode[],
  allEdges: Edge[],
) => {
  const childrenNodes = childrenNodeIDs
    .map((id) => allNodes.find((n) => n.id === id))
    .filter((n) => n !== undefined) as BeMappedNode[];
  const edgesInChildren = getEdgesInSubgraph(allEdges, childrenNodes);
  const [nodes, _] = orderNodesAndEdgesForLayout(
    childrenNodes,
    edgesInChildren,
  );

  return getCreatableNodes(nodes);
};

type GroupIconProps = { size?: NodeIconSize; groupId: string };

export const GroupIcon = observer(
  ({ size = "lg", groupId }: GroupIconProps) => {
    const { nodesArray, groups, edgesArray } = useGraphStore();
    const groupNode = groups.get(groupId);
    if (!groupNode) {
      return <></>;
    }

    const groupChildren = getCreatableChildrenOrdered(
      groupNode.data.childrenIDs,
      nodesArray,
      edgesArray,
    );

    const colors = groupChildren
      .slice(0, 4)
      .map(
        (child): NodeIconColor =>
          child.type
            ? getNodeTypeBackground(child.type as NODE_TYPE, true)
            : "gray-200",
      );

    return <NodeIcon colors={colors} size={size} />;
  },
);

type NodeIconProps = { size?: NodeIconSize } & (
  | {
      nodeType:
        | SimpleCreatableNodeType
        | NODE_TYPE.REVIEW_CONNECTION_NODE
        | NODE_TYPE.CASE_CREATION_NODE
        | NODE_TYPE.CASE_UPDATE_NODE
        | NODE_TYPE.READ_ENTITY_NODE
        | NODE_TYPE.WRITE_ENTITY_NODE
        | AddNodeGroupType
        | ConnectionGroupType;
    }
  | {
      nodeType: ConnectNodeType;
      provider: Provider | string;
      resource?: Resource | string;
    }
  | { nodeType: NODE_TYPE.GROUP_NODE; groupId: string }
  | {
      nodeType: NODE_TYPE.MANIFEST_CONNECTION_NODE;
      provider: string;
    }
  | {
      nodeType: ConnectNodeType;
      provider: Provider | string;
      mediaKey: string | null;
    }
);

export const NodeTypeIcon = (params: NodeIconProps): React.ReactElement => {
  const backgroundColor = getNodeTypeBackground(params.nodeType);
  switch (params.nodeType) {
    case NODE_TYPE.AI_NODE_V2: {
      return (
        <NodeIcon
          color={backgroundColor}
          icon={faSparkles}
          size={params.size}
        />
      );
    }
    case NODE_TYPE.AGENT_NODE: {
      return (
        <NodeIcon color={backgroundColor} icon={faRobot} size={params.size} />
      );
    }
    case NODE_TYPE.RULE_NODE_V2: {
      return (
        <NodeIcon color={backgroundColor} icon={faShuffle} size={params.size} />
      );
    }
    case NODE_TYPE.SPLIT_NODE_V2: {
      return (
        <NodeIcon color={backgroundColor} icon={faSplit} size={params.size} />
      );
    }
    case NODE_TYPE.CODE_NODE: {
      return (
        <NodeIcon color={backgroundColor} icon={faCode} size={params.size} />
      );
    }
    case NODE_TYPE.ML_NODE: {
      return (
        <NodeIcon
          color={backgroundColor}
          icon={faMicrochip}
          size={params.size}
        />
      );
    }
    case NODE_TYPE.MANIFEST_CONNECTION_NODE: {
      return <ProviderIcon provider={params.provider} size={params.size} />;
    }
    case NODE_TYPE.CUSTOM_CONNECTION_NODE:
    case NODE_TYPE.WEBHOOK_CONNECTION_NODE:
    case NODE_TYPE.SQL_DATABASE_CONNECTION_NODE:
    case NODE_TYPE.INTEGRATION_NODE: {
      if ("mediaKey" in params) {
        return (
          <ProviderIcon
            mediaKey={params.mediaKey}
            provider={params.provider}
            size={params.size}
          />
        );
      }
      if (!params.resource) {
        return <ProviderIcon provider={params.provider} size={params.size} />;
      } else {
        return (
          <ResourceIcon
            provider={params.provider}
            resource={params.resource}
            size={params.size}
          />
        );
      }
    }
    case NODE_TYPE.DECISION_TABLE_NODE: {
      return (
        <NodeIcon
          color={backgroundColor}
          icon={faTableRows}
          size={params.size}
        />
      );
    }
    case NODE_TYPE.MATRIX_TABLE_NODE: {
      return (
        <NodeIcon color={backgroundColor} icon={faTable} size={params.size} />
      );
    }
    case NODE_TYPE.ASSIGNMENT_NODE: {
      return (
        <NodeIcon color={backgroundColor} icon={faEquals} size={params.size} />
      );
    }
    case NODE_TYPE.SCORECARD_NODE: {
      return (
        <NodeIcon
          color={backgroundColor}
          icon={faCalculator}
          size={params.size}
        />
      );
    }
    case NODE_TYPE.GROUP_NODE: {
      return <GroupIcon groupId={params.groupId} size={params.size} />;
    }
    case NODE_TYPE.FLOW_NODE: {
      return (
        <NodeIcon
          color={backgroundColor}
          icon={faDiagramNext}
          size={params.size}
        />
      );
    }
    case NODE_TYPE.REVIEW_CONNECTION_NODE: {
      return (
        <NodeIcon
          color={backgroundColor}
          icon={faCircleUser}
          size={params.size}
        />
      );
    }
    case NODE_TYPE.LOOP_NODE: {
      return (
        <NodeIcon
          color={backgroundColor}
          icon={faRotateRight}
          size={params.size}
        />
      );
    }

    case ConnectionGroupType.CASE_ACTIONS:
    case NODE_TYPE.CASE_CREATION_NODE:
    case NODE_TYPE.CASE_UPDATE_NODE: {
      return (
        <NodeIcon
          color={backgroundColor}
          icon={faBriefcase}
          size={params.size}
        />
      );
    }
    case AddNodeGroupType.ENTITY_ACTIONS:
    case NODE_TYPE.WRITE_ENTITY_NODE:
    case NODE_TYPE.READ_ENTITY_NODE: {
      return (
        <NodeIcon color={backgroundColor} icon={faCube} size={params.size} />
      );
    }
    default:
      assertUnreachable(params);
  }
  return <></>;
};

const getNodeTypeBackground = (
  nodeType: NODE_TYPE | AddNodeGroupType | ConnectionGroupType,
  forGroupIcon: boolean = false,
): NodeIconColor => {
  switch (nodeType) {
    case NODE_TYPE.RULE_NODE_V2:
      return "green-electric";
    case NODE_TYPE.SPLIT_NODE_V2:
      return "indigo-200";
    case NODE_TYPE.CODE_NODE:
      return "yellow-200";
    case NODE_TYPE.ML_NODE:
      return "sky-200";
    case NODE_TYPE.DECISION_TABLE_NODE:
      return "orange-200";
    case NODE_TYPE.MATRIX_TABLE_NODE:
      return "indigo-200";
    case NODE_TYPE.ASSIGNMENT_NODE:
      return "pink-200";
    case NODE_TYPE.SCORECARD_NODE:
      return "green-200";
    case NODE_TYPE.LOOP_NODE:
    case NODE_TYPE.FLOW_NODE:
      return forGroupIcon ? "gray-200" : "outlined";
    case NODE_TYPE.REVIEW_CONNECTION_NODE:
      return "indigo-100";
    case NODE_TYPE.AI_NODE_V2:
      return "sky-200";
    case NODE_TYPE.AGENT_NODE:
      return "orange-100";
    case AddNodeGroupType.ENTITY_ACTIONS:
    case NODE_TYPE.WRITE_ENTITY_NODE:
    case NODE_TYPE.READ_ENTITY_NODE:
      return "green-200";
    case ConnectionGroupType.CASE_ACTIONS:
    case NODE_TYPE.CASE_CREATION_NODE:
    case NODE_TYPE.CASE_UPDATE_NODE:
      return "yellow-100";
    case NODE_TYPE.INPUT_NODE:
    case NODE_TYPE.OUTPUT_NODE:
    case NODE_TYPE.CUSTOM_CONNECTION_NODE:
    case NODE_TYPE.WEBHOOK_CONNECTION_NODE:
    case NODE_TYPE.INTEGRATION_NODE:
    case NODE_TYPE.MANIFEST_CONNECTION_NODE:
    case NODE_TYPE.SQL_DATABASE_CONNECTION_NODE:
    case NODE_TYPE.SPLIT_BRANCH_NODE_V2:
    case NODE_TYPE.SPLIT_MERGE_NODE:
    case NODE_TYPE.GROUP_NODE:
    case NODE_TYPE.GROUP_PRE_SEPARATOR_NODE:
    case NODE_TYPE.GROUP_POST_SEPARATOR_NODE:
      return "gray-200";
    default:
      return "gray-200";
  }
};

export const getNodeIconFromNode = (
  node: BeMappedNode,
  size?: NodeIconSize,
) => {
  if (node.type === undefined) return;
  if (node.type === NODE_TYPE.INTEGRATION_NODE) {
    const nodeData = node.data as IntegrationNodeData;
    return (
      <NodeTypeIcon
        nodeType={node.type}
        provider={nodeData.providerResource.provider}
        resource={nodeData.providerResource.resource}
        size={size}
      />
    );
  }
  if (node.type === NODE_TYPE.MANIFEST_CONNECTION_NODE) {
    const nodeData = node.data as ManifestConnectionNodeData;
    return (
      <NodeTypeIcon
        nodeType={node.type}
        provider={nodeData.providerResource.provider}
        size={size}
      />
    );
  }

  if (node.type === NODE_TYPE.WEBHOOK_CONNECTION_NODE) {
    const nodeData = node.data as InboundWebhookConnectionNodeData;
    return (
      <NodeTypeIcon
        mediaKey={nodeData?.mediaKey}
        nodeType={node.type}
        provider={nodeData.providerResource.provider}
        resource={nodeData.providerResource.resource}
        size={size}
      />
    );
  }
  if (node.type === NODE_TYPE.SQL_DATABASE_CONNECTION_NODE) {
    const nodeData = node.data as DatabaseConnectionNodeData;
    return (
      <NodeTypeIcon
        nodeType={node.type}
        provider={nodeData.providerResource.provider}
        resource={nodeData.providerResource.resource}
        size={size}
      />
    );
  }
  if (node.type === NODE_TYPE.CUSTOM_CONNECTION_NODE) {
    const nodeData = node.data as CustomConnectionNodeData;
    return (
      <NodeTypeIcon
        mediaKey={nodeData?.mediaKey}
        nodeType={node.type}
        provider={nodeData.providerResource.provider}
        resource={nodeData.providerResource.resource}
        size={size}
      />
    );
  }
  if (
    (SIMPLE_CREATABLE_NODE_TYPES as readonly string[]).includes(node.type) ||
    node.type === NODE_TYPE.REVIEW_CONNECTION_NODE ||
    node.type === NODE_TYPE.CASE_CREATION_NODE ||
    node.type === NODE_TYPE.CASE_UPDATE_NODE ||
    node.type === NODE_TYPE.READ_ENTITY_NODE ||
    node.type === NODE_TYPE.WRITE_ENTITY_NODE
  ) {
    return (
      <NodeTypeIcon
        nodeType={node.type as SimpleCreatableNodeType}
        size={size}
      />
    );
  }
  return <></>;
};
